// Menu MFE - Modernized App.tsx
// Based on diagnostics MFE pattern

import React from 'react';
import { HashRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Menu } from './pages/Menu';
import { MenuIOS } from './pages/MenuIOS';
import { AccountInformation } from './pages/AccountInformation';
import { AccountInformationIOS } from './pages/AccountInformationIOS';
import { About } from './pages/About';
import { AboutIOS } from './pages/AboutIOS';
import { Privacy } from './pages/Privacy';
import { PrivacyIOS } from './pages/PrivacyIOS';
import { Terms } from './pages/Terms';
import { TermsIOS } from './pages/TermsIOS';
import {
  CommunicationBridge,
  useBridgeRouteObserver,
  registerForNavigationDeeplink,
  registerForNavigationGoBack,
  registerForLifecycleEvents,
} from '@sequelae/lore-mfe-kit';
import { postMessageService } from './services/postMessage';
import { NavigationModeProvider } from './contexts/NavigationModeContext';
import '@sequelae/lore-core/dist/styles/index.css';
import './index.css';

type CommonMfeProps = {
  hostAuthResult: any;
  externalRefUserId: string;
  onFinished?: () => void;
};

function App(props: CommonMfeProps) {
  // Note: useClickEventTracker is disabled for standalone mode
  // as it requires Redux Provider from bridge context

  // Initialize auth token from hostAuthResult
  React.useEffect(() => {
    console.log('[MenuMFE] Received props:', {
      hasHostAuthResult: !!props.hostAuthResult,
      externalRefUserId: props.externalRefUserId,
    });

    // Extract and set the access token from hostAuthResult
    if (props.hostAuthResult) {
      const token =
        props.hostAuthResult.access_token ||
        props.hostAuthResult.accessToken ||
        props.hostAuthResult;

      if (typeof token === 'string' && token) {
        console.log('[MenuMFE] ✅ Storing access token from hostAuthResult');
        // Store in localStorage for services to use
        window.localStorage.setItem('access_token', token);
      } else {
        console.warn(
          '[MenuMFE] ⚠️ hostAuthResult does not contain a valid token:',
          typeof props.hostAuthResult
        );
      }
    } else {
      // Fallback: try to get token from bridge
      const bridgeToken = CommunicationBridge.getAccessToken();

      if (bridgeToken) {
        console.log('[MenuMFE] ✅ Storing token from CommunicationBridge');
        window.localStorage.setItem('access_token', bridgeToken);
      } else {
        console.log('[MenuMFE] ℹ️ No token from bridge, will use bridge on demand');
      }
    }
  }, [props.hostAuthResult]);

  // Make postMessage service globally available
  React.useEffect(() => {
    (window as any).postMessageService = postMessageService;
    (window as any).sendMessageToNativeApp = (message: any) =>
      postMessageService.sendToNativeApp(message);

    return () => {
      delete (window as any).postMessageService;
      delete (window as any).sendMessageToNativeApp;
    };
  }, []);

  // Get ref_user_id
  const urlParams = new URLSearchParams(window.location.search);
  const urlUserId = urlParams.get('ref_user_id');
  const localStorageUserId = window.localStorage.getItem('ref_user_id');

  const refUserId = React.useMemo(() => {
    const resolved = props.externalRefUserId || urlUserId || localStorageUserId || '44016';
    console.log('[MenuMFE] Final resolved User ID:', resolved);
    return resolved;
  }, [props.externalRefUserId, urlUserId, localStorageUserId]);

  // Store in localStorage
  React.useEffect(() => {
    if (refUserId && refUserId !== localStorageUserId) {
      window.localStorage.setItem('ref_user_id', refUserId);
      console.log('[MenuMFE] Stored refUserId in localStorage:', refUserId);
    }
  }, [refUserId, localStorageUserId]);

  return (
    <NavigationModeProvider>
      <HashRouter>
        <AppContent userId={refUserId} onFinished={props.onFinished} />
      </HashRouter>
    </NavigationModeProvider>
  );
}

// Valid internal routes for Menu MFE (includes both iOS and Android variants)
const VALID_INTERNAL_ROUTES = [
  '/',
  '/menu',
  '/ios',
  '/account',
  '/account-ios',
  '/about',
  '/about/privacy',
  '/about/terms',
  '/about-ios',
  '/about-ios/privacy',
  '/about-ios/terms',
  '/test-navigation',
] as const;

// Check if a route is external (should be handled by bridge)
const isExternalRoute = (path: string): boolean => {
  const externalRoutePatterns = [
    /^\/rewards\//,
    /^\/community\//,
    /^\/explorations\//,
    /^\/lorebot\//,
    /^\/diagnostics\//,
    /^\/streaks/,
    /^\/verification/,
  ];
  return externalRoutePatterns.some(pattern => pattern.test(path));
};

// Separate component to use Router hooks
function AppContent({ userId, onFinished }: { userId: string; onFinished?: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize bridge route observer for external navigation (inside Router context)
  useBridgeRouteObserver([
    /^\/rewards\//,
    /^\/community\//,
    /^\/explorations\//,
    /^\/lorebot\//,
    /^\/diagnostics\//,
    /^\/verification\//,
    /^\/streaks/,
  ]);

  // Register for back navigation (LEGACY WEB-LIKE mode)
  // In legacy web-like mode, the MFE manages its own back stack and signals native when finished
  registerForNavigationGoBack({
    onGoBack: () => {
      const currentPath = location.pathname;
      const isAtRoot = currentPath === '/menu' || currentPath === '/ios' || currentPath === '/';

      if (isAtRoot) {
        console.log('⬅️ Already at root route, will signal native to close MFE');
        return false; // Tell native to pop/close the screen
      }

      console.log('⬅️ Navigating back from:', currentPath);
      navigate(-1);
      return true; // We handled it internally
    },
    autoFinishWhenAtRoot: true, // Automatically calls CommunicationBridge.onFinished() when at root
  });

  // Register for deep link navigation
  registerForNavigationDeeplink({
    onDeeplink: (path: string, data?: any) => {
      console.log('🔗 Deep link navigation requested via hook:', path, data);
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;

      if (isExternalRoute(normalizedPath)) {
        console.log('ℹ️ Deep link path is external, should be handled by bridge:', normalizedPath);
        return;
      }

      // Validate route
      if (!(VALID_INTERNAL_ROUTES as readonly string[]).includes(normalizedPath)) {
        console.warn('⚠️ Invalid internal route for deep link:', normalizedPath);
        return;
      }

      if (location.pathname === normalizedPath) {
        console.log('ℹ️ Already on target route:', normalizedPath);
        return;
      }

      console.log('🔗 Navigating to deep link route:', normalizedPath);
      navigate(normalizedPath);
    },
  });

  // Register for lifecycle events
  registerForLifecycleEvents({
    onViewDidAppear: () => {
      console.log('🔄 Lifecycle: viewDidAppear');
    },
    onViewDidDisappear: () => {
      console.log('🔄 Lifecycle: viewDidDisappear');
    },
  });

  return (
    <div className="App">
      <div className="content">
        <Routes>
          <Route path="/" element={<Navigate to="/menu" replace />} />
          <Route path="/menu" element={<Menu />} />
          <Route path="/ios" element={<MenuIOS />} />
          <Route path="/account" element={<AccountInformation />} />
          <Route path="/account-ios" element={<AccountInformationIOS />} />
          <Route path="/about" element={<About />} />
          <Route path="/about/privacy" element={<Privacy />} />
          <Route path="/about/terms" element={<Terms />} />
          <Route path="/about-ios" element={<AboutIOS />} />
          <Route path="/about-ios/privacy" element={<PrivacyIOS />} />
          <Route path="/about-ios/terms" element={<TermsIOS />} />
        </Routes>
      </div>
    </div>
  );
}

export default App;
